import { computed, observable } from "mobx";
import {
  model,
  Model,
  modelAction,
  prop,
  idProp,
  frozen,
  Frozen,
  modelFlow,
  _async,
  _await,
} from "mobx-keystone";
import { createContext } from "react";

@model("megalithic/GetAChannel")
export class GetAChannel extends Model({
  id: idProp,
  hasLoaded: prop<boolean>(false),
  selectedNetwork: prop<"Mainnet" | "MutinyNet" | undefined>(undefined),
  userHasConfirmedIsConnected: prop<boolean>(false),
  getInfoData: prop<Frozen<any> | undefined>(undefined),
  getInfoDataHasLoaded: prop<boolean>(false),
  orderErrored: prop<boolean>(false),
  channelSize: prop<number>(1_000_000),
  useZeroConf: prop<boolean>(false),
  isPrivateChannel: prop<boolean>(true),
  publicKey: prop<string>(""),
  orderData: prop<Frozen<any> | undefined>(undefined),
  orderError: prop<Frozen<any> | undefined>(undefined),
  pollingStatus: prop<string>("idle"),
  orderStatus: prop<string>(""),
}) {
  @modelAction
  resetGetAChannelToDefaultValues() {
    this.orderErrored = false;
    this.selectedNetwork = undefined;
    this.userHasConfirmedIsConnected = false;
    this.getInfoData = undefined;
    this.getInfoDataHasLoaded = false;
    this.channelSize = 1_000_000;
    this.useZeroConf = false;
    this.isPrivateChannel = true;
    this.publicKey = "";
    this.orderData = undefined;
    this.orderError = undefined;
    this.pollingStatus = "idle";
    this.orderStatus = "";
  }

  @computed
  get nodeAddress() {
    if (this.getInfoData?.data?.uris?.[0]) {
      return this.getInfoData.data.uris[0];
    } else {
      console.error("Node address (uris) is missing in getInfoData");
      return "";
    }
  }
  @computed
  get userHasInputtedValidPublicKey() {
    return this.publicKey.length > 0 && this.publicKeyIsValid;
  }

  @computed
  get publicKeyIsValid() {
    const regex = /^[0-9a-fA-F]{66}$/;
    return regex.test(this.publicKey);
  }
  @computed
  get errorMessageForPublicKey() {
    if (this.publicKey.length === 0) {
      return null;
    } else if (!this.publicKeyIsValid) {
      return "Invalid node public key.";
    } else {
      return null;
    }
  }

  @computed
  get minChannelSize() {
    if (this.getInfoData?.data?.min_initial_lsp_balance_sat !== undefined) {
      const value = parseInt(
        this.getInfoData.data.min_initial_lsp_balance_sat,
        10
      );
      return isNaN(value) ? 150000 : value; // Fallback to 150000 if invalid
    } else {
      console.error("min_initial_lsp_balance_sat is missing in getInfoData");
      return 150000; // Fallback
    }
  }

  @computed
  get maxChannelSize() {
    if (this.getInfoData?.data?.max_initial_lsp_balance_sat !== undefined) {
      const value = parseInt(
        this.getInfoData.data.max_initial_lsp_balance_sat,
        10
      );
      return isNaN(value) ? 16000000 : value; // Fallback to 16000000 if invalid
    } else {
      console.error("max_initial_lsp_balance_sat is missing in getInfoData");
      return 16000000; // Fallback
    }
  }

  @modelAction
  setHasLoaded(value: boolean) {
    this.hasLoaded = value;
  }

  @modelAction
  hydrateDataFromPage() {
    const rootDiv = document.getElementById("root");
    if (rootDiv?.dataset.hydrate) {
      const json = JSON.parse(rootDiv.dataset.hydrate);
      this.getInfoData = frozen(json);
      this.setHasLoaded(true);
    }
  }

  @modelAction
  setSelectedNetwork(value: "Mainnet" | "MutinyNet") {
    if (this.selectedNetwork === value) {
      this.selectedNetwork = undefined;
    } else {
      this.selectedNetwork = value;
    }
  }

  @modelAction
  setuserHasConfirmedIsConnected(value: boolean) {
    this.userHasConfirmedIsConnected = value;
    if (value) {
      this.fetchGetInfo();
    }
  }

  @modelAction
  setChannelSize(value: number) {
    const min = this.minChannelSize;
    const max = this.maxChannelSize;
    this.channelSize = Math.max(min, Math.min(max, Math.round(value)));
  }

  // Add this computed property
  @computed
  get isPrivateChannelDisabled() {
    return this.useZeroConf;
  }

  // Modify the setUseZeroConf action
  @modelAction
  setUseZeroConf(useZeroConf: boolean) {
    this.useZeroConf = useZeroConf;

    // If enabling zero-conf, force private channel on
    if (useZeroConf) {
      this.isPrivateChannel = true;
    }
  }

  @modelAction
  setIsPrivateChannel(value: boolean) {
    this.isPrivateChannel = value;
  }

  @modelAction
  setPublicKey(value: string) {
    this.publicKey = value;
  }

  @modelAction
  setOrderData(data: any) {
    this.orderData = frozen(data);
  }

  @modelAction
  setPollingStatus(status: string) {
    this.pollingStatus = status;
  }

  @modelAction
  setOrderStatus(status: string) {
    this.orderStatus = status;
  }

  @computed
  get baseUrl() {
    return this.selectedNetwork === "Mainnet"
      ? "https://megalithic.me/api/lsps1/v1"
      : "https://lsp1.mutiny.megalith-node.com/api/lsps1/v1";
  }

  @modelFlow
  fetchGetInfo = _async(function* (this: GetAChannel) {
    const url = `${this.baseUrl}/get_info`;
    try {
      const response = yield* _await(fetch(url));
      const data = yield* _await(response.json());
      this.getInfoData = frozen(data);
      console.log("getInfoData:", this.getInfoData.data);
      this.getInfoDataHasLoaded = true;
    } catch (error) {
      console.error("Failed to fetch get_info:", error);
    }
  });
  @modelFlow
  createOrder = _async(function* (this: GetAChannel) {
    const url = `${this.baseUrl}/create_order`;
    console.log("in create order with ", url);
    const payload = {
      lsp_balance_sat: this.channelSize.toString(), // Convert back to string for API
      client_balance_sat: "0",
      required_channel_confirmations: this.useZeroConf ? 0 : 1,
      funding_confirms_within_blocks: 6,
      channel_expiry_blocks: 13140,
      token: "megalithic-website-order",
      announce_channel: !this.isPrivateChannel,
      public_key: this.publicKey,
    };

    try {
      const response = yield* _await(
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        })
      );
      const data = yield* _await(response.json());
      console.log(
        "got response from createOrder, either put this in orderData or orderError ",
        data
      );

      // Check if response contains an error key
      if (data.error) {
        this.orderErrored = true; // Set orderErrored to true
        this.orderError = frozen(data); // Set orderError with frozen error data
      } else {
        this.orderData = frozen(data); // Set orderData for successful response
      }
    } catch (error) {
      console.error("Failed to create order:", error);
      alert("Failed to create order. Please try again.");
      this.orderError = frozen({
        error: { message: "Network or fetch error occurred" },
      }); // Optional: Set orderError for fetch errors
    }
  });

  @modelFlow
  pollOrderStatus = _async(function* (this: GetAChannel) {
    const url = `${this.baseUrl}/get_order?order_id=${this.orderData?.data.order_id}`;

    this.pollingStatus = "polling";
    const startTime = Date.now();

    while (Date.now() - startTime < 900000) {
      try {
        const response = yield* _await(fetch(url));
        const data = yield* _await(response.json());
        console.log("data in pollOrderStatus:", data);
        this.orderStatus = data.order_state;
        if (data.order_state === "COMPLETED") {
          this.pollingStatus = "completed";
          return;
        }
      } catch (error) {
        console.error("Polling failed:", error);
      }
      yield* _await(new Promise((resolve) => setTimeout(resolve, 3000)));
    }
    this.pollingStatus = "timeout";
  });
}

export const runCreateGetAChannel = () => {
  const getAChannel = new GetAChannel({});
  return getAChannel;
};

const createdGetAChannel = runCreateGetAChannel();
export const GetAChannelContext =
  createContext<GetAChannel>(createdGetAChannel);

export interface GetAChannelInterface {
  GetAChannel: GetAChannel;
}
